import { cs } from '../../../lib/currency';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Checkbox } from '../../../components/ui/checkbox';
import PaymentStatusBadge from '../../../components/PaymentStatusBadge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Search,
  Loader2,
  DollarSign,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Mail,
  Check,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { toQueryParams } from './DateRangePicker';

// iter302: deterministic toFixed(2) — see Transactions.js notes.
const formatMoney = (v) => {
  const n = Number(v);
  const safe = Number.isFinite(n) ? n : 0;
  const [whole, dec] = safe.toFixed(2).split('.');
  const sign = whole.startsWith('-') ? '-' : '';
  const abs = sign ? whole.slice(1) : whole;
  return `${sign}${cs()}${abs.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${dec}`;
};

const statusBadge = (status) => <PaymentStatusBadge status={status} />;

const STREAM_META = {
  subscriptions: {
    label: 'Subscriptions',
    description: 'Monthly program / enrollment fees',
    bulkType: 'enrollment',
    nameField: ['player_name', 'parent_name'],
    subField: 'program_name',
  },
  lane_bookings: {
    label: 'Lane Bookings',
    description: 'Lane rental revenue',
    bulkType: 'lane_booking',
    nameField: ['customer_name'],
    subField: null,
  },
  tournaments: {
    label: 'Tournaments',
    description: 'Tournament registrations',
    bulkType: 'tournament',
    nameField: ['player_name', 'parent_name'],
    // Tournament items don't have `description` on the server — the
    // meaningful thing to show in that column is the tournament name.
    subField: 'tournament_name',
    // And there's no `date` field either; the date-ish info lives in
    // `paid_at` (for paid rows) or `registration_date` (fallback).
    dateField: ['paid_at', 'registration_date'],
  },
  coaching: {
    label: 'Coaching',
    description: '1:1 sessions, group coaching',
    bulkType: 'coaching',
    nameField: ['customer_name', 'parent_name', 'player_name'],
    subField: 'description',
    dateField: ['date', 'payment_due_date'],
  },
};

export const BillingStreamDetail = ({ stream, dateRange, locationId }) => {
  const meta = STREAM_META[stream];
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ summary: {}, items: [], total: 0 });
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const limit = 50;

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = toQueryParams(dateRange);
      params.append('stream', stream);
      if (locationId && locationId !== 'all') params.append('location_id', locationId);
      if (searchDebounced) params.append('search', searchDebounced);
      if (statusFilter && statusFilter !== 'all') params.append('status_filter', statusFilter);
      params.append('sort_by', sortBy);
      params.append('sort_dir', sortDir);
      params.append('page', String(page));
      params.append('limit', String(limit));

      const { data: resp } = await api.get(`/admin/billing/stream-detail?${params.toString()}`);
      setData(resp);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [stream, dateRange, locationId, searchDebounced, statusFilter, sortBy, sortDir, page]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // Reset page when filters change.
  useEffect(() => { setPage(1); setSelected(new Set()); }, [searchDebounced, statusFilter, sortBy, sortDir, stream, dateRange, locationId]);

  const toggleRow = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === data.items.length) setSelected(new Set());
    else setSelected(new Set(data.items.map((i) => i.id)));
  };

  const runBulk = async (action) => {
    if (selected.size === 0) return;
    const label = action === 'mark_paid' ? 'mark paid' : 'send reminder';
    if (!window.confirm(`${action === 'mark_paid' ? 'Mark' : 'Send reminder to'} ${selected.size} selected item(s)?`)) return;
    setBulkLoading(true);
    try {
      const items = Array.from(selected).map((id) => ({ type: meta.bulkType, id }));
      const url = action === 'mark_paid'
        ? '/admin/billing/bulk-mark-paid'
        : '/admin/billing/bulk-send-reminder';
      const { data: resp } = await api.post(url, { items });
      toast.success(resp.message || `${label} complete`);
      setSelected(new Set());
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${label}`);
    } finally {
      setBulkLoading(false);
    }
  };

  const s = data.summary || {};
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / limit));

  const getName = (item) => {
    for (const f of meta.nameField) {
      if (item[f]) return item[f];
    }
    return '—';
  };

  // Click a column header → toggle asc/desc for that field. Clicking a new
  // column starts in ascending order to match user expectation.
  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'date' || key === 'amount' ? 'desc' : 'asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3" />
      : <ArrowDown className="h-3 w-3" />;
  };

  const SortableHeader = ({ column, label, align = 'left', testId }) => (
    <TableHead className={align === 'right' ? 'text-right' : ''}>
      <button
        type="button"
        onClick={() => handleSort(column)}
        className={`inline-flex items-center gap-1 font-medium text-slate-700 hover:text-slate-900 hover:underline select-none ${align === 'right' ? 'justify-end w-full' : ''}`}
        data-testid={testId || `${stream}-sort-${column}`}
      >
        {label}
        <SortIcon column={column} />
      </button>
    </TableHead>
  );

  /**
   * When the admin clicks a customer name in the detail table, jump to
   * the right detail page:
   *   - For lane bookings, the row carries `customer_id` (the user who
   *     booked). We open `/admin/customers/{id}`. That page detects if
   *     the user actually has children/enrollments and auto-redirects to
   *     `/admin/parents/{id}?tab=billing` — so admins land on the richer
   *     parent view if it applies, otherwise on a dedicated customer
   *     detail page (no Children / Enrollments tabs).
   *   - For other streams (enrollments, tournaments, camps, etc.) the
   *     row carries `parent_id` and we go straight to parent detail.
   *   - If no ID is known (legacy walk-ins), fall back to a no-op.
   */
  const openCustomer = (item) => {
    const parentId = item.parent_id;
    if (parentId) {
      navigate(`/admin/parents/${parentId}?tab=billing`);
      return;
    }
    const customerId = item.customer_id;
    if (customerId) {
      navigate(`/admin/customers/${customerId}`);
      return;
    }
    toast.info('No account on file for this record');
  };

  return (
    <div className="space-y-4" data-testid={`stream-detail-${stream}`}>
      {/* Summary cards — 4 per period */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">Total Revenue</p>
              <DollarSign className="h-4 w-4 text-emerald-500" />
            </div>
            <p className="text-2xl font-bold text-emerald-700" data-testid={`${stream}-total-revenue`}>
              {formatMoney(s.total_revenue)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">This Period</p>
              <CheckCircle2 className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-blue-700" data-testid={`${stream}-revenue-this-period`}>
              {formatMoney(s.revenue_this_month)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Pending ({s.pending_count || 0})
              </p>
              <RefreshCw className="h-4 w-4 text-amber-500" />
            </div>
            <p className="text-2xl font-bold text-amber-700">{formatMoney(s.pending)}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-red-700">
                Outstanding ({s.outstanding_count || 0})
              </p>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-700" data-testid={`${stream}-outstanding`}>
              {formatMoney(s.outstanding)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by customer name…"
              className="pl-8"
              data-testid={`${stream}-search`}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid={`${stream}-status-filter`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="outstanding">Outstanding</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={`${sortBy}:${sortDir}`}
            onValueChange={(v) => {
              const [b, d] = v.split(':');
              setSortBy(b); setSortDir(d);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date:desc">Newest First</SelectItem>
              <SelectItem value="date:asc">Oldest First</SelectItem>
              <SelectItem value="amount:desc">Amount (High → Low)</SelectItem>
              <SelectItem value="amount:asc">Amount (Low → High)</SelectItem>
              <SelectItem value="customer:asc">Customer (A → Z)</SelectItem>
              <SelectItem value="status:asc">Status</SelectItem>
            </SelectContent>
          </Select>
          {selected.size > 0 && (
            <div className="flex items-center gap-2 ml-auto" data-testid={`${stream}-bulk-bar`}>
              <span className="text-sm text-slate-600 font-medium">{selected.size} selected</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => runBulk('mark_paid')}
                disabled={bulkLoading}
                data-testid={`${stream}-bulk-mark-paid`}
              >
                <Check className="h-3.5 w-3.5 mr-1" />
                Mark Paid
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => runBulk('send_reminder')}
                disabled={bulkLoading}
                data-testid={`${stream}-bulk-send-reminder`}
              >
                <Mail className="h-3.5 w-3.5 mr-1" />
                Send Reminder
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center">
              <Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" />
            </div>
          ) : data.items.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              No records match your filters.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table data-testid={`${stream}-table`}>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={selected.size === data.items.length && data.items.length > 0}
                          onCheckedChange={toggleAll}
                          data-testid={`${stream}-select-all`}
                        />
                      </TableHead>
                      <SortableHeader column="customer" label="Customer" />
                      <SortableHeader column="date" label="Date" />
                      <TableHead>Description</TableHead>
                      <SortableHeader column="amount" label="Amount" />
                      <SortableHeader column="status" label="Status" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((item) => (
                      <TableRow key={item.id} className="hover:bg-slate-50">
                        <TableCell>
                          <Checkbox
                            checked={selected.has(item.id)}
                            onCheckedChange={() => toggleRow(item.id)}
                            data-testid={`${stream}-select-${item.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          {(item.parent_id || item.customer_id) ? (
                            <button
                              type="button"
                              onClick={() => openCustomer(item)}
                              className="font-medium text-blue-700 hover:text-blue-900 hover:underline text-left"
                              data-testid={`${stream}-customer-link-${item.id}`}
                              title="Open billing tab for this customer"
                            >
                              {getName(item)}
                            </button>
                          ) : (
                            <span className="font-medium">{getName(item)}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {(() => {
                            // Prefer the stream's explicit dateField list
                            // (e.g. tournaments use `paid_at` / `registration_date`);
                            // fall back to the generic enrollment/lane fields.
                            const candidates = meta.dateField || ['date', 'payment_due_date'];
                            for (const f of candidates) {
                              if (item[f]) {
                                // Trim ISO timestamps to YYYY-MM-DD for readability.
                                const v = String(item[f]);
                                return v.length >= 10 && v[4] === '-' ? v.slice(0, 10) : v;
                              }
                            }
                            return '—';
                          })()}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {meta.subField ? (item[meta.subField] || '—') : (
                            item.start_time ? `${item.start_time} · ${item.duration || ''}` : (item.description || '—')
                          )}
                        </TableCell>
                        <TableCell className="font-semibold">{formatMoney(item.amount)}</TableCell>
                        <TableCell>{statusBadge(item.payment_status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between p-3 border-t text-sm text-slate-600">
                <div>
                  Showing {(page - 1) * limit + 1}–{Math.min(page * limit, data.total)} of {data.total}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid={`${stream}-prev-page`}
                  >
                    Prev
                  </Button>
                  <span>Page {page} / {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => p + 1)}
                    data-testid={`${stream}-next-page`}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
